<template>
  <div data-testid="pdv-entregas-page-root">
    <PDVEntregasKpiCards :kpis="kpis" :active-kpi="activeKpi" @toggle-kpi="toggleKpi" />

    <section class="items-section">
      <h3>{{ t('Itens em aberto') }}</h3>
      <div v-if="!isLoading && filteredRows.length === 0" class="empty-state">
        <i class="pi pi-inbox"></i>
        <p>{{ activeKpi ? t('Nenhum item para este indicador') : t('Nenhum item em aberto') }}</p>
      </div>
      <DataTable
        v-else
        :config="itemsTableConfig"
        :data="filteredRows"
        :refresh-button="{ show: true, text: t('Atualizar') }"
        data-testid="pdv-entregas-items-table"
        @refresh="loadAll"
        @button-click="handleItemAction"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../grid/DataTable.vue';
import { eventBus } from '../../eventBus';
import { useSOEntregas } from '../../composables/useSOEntregas';
import { useEntregasKpiFilter } from '../../composables/useEntregasKpiFilter';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { log, error as logError } from '../../utils/DebugLogger';
import type { Orders_EntregasKpisResponseDados } from '../../contracts/Orders_EntregasKpisResponse';
import type { Orders_EntregasItemRow } from '../../contracts/Orders_EntregasItemRow';

const { t } = useI18n();
const { formatDate, fromApiDateTimeFormat, fromApiDateToDate } = useDateFormatter();
const {
  fetchEntregasKpis,
  fetchEntregasItems,
  isLoading,
} = useSOEntregas();

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const kpis = ref<Orders_EntregasKpisResponseDados>({
  today_and_next_2_days: 0,
  delayed: 0,
  rescheduled: 0,
  pending_alerts: 0,
  pending_delivery: 0,
});
const rawItems = ref<Orders_EntregasItemRow[]>([]);
const items = computed(() =>
  rawItems.value.map(r => decorateEntregasRow(r)) as unknown as Orders_EntregasItemRow[]
);

// KPI click filtering — same predicates that back the KPI counts (see composable).
const { activeKpi, toggleKpi, filteredRows } = useEntregasKpiFilter(items);

// ---------------------------------------------------------------------------
// Status badge maps
// ---------------------------------------------------------------------------

function itemStatusLabel(status: string): string {
  const map: Record<string, string> = {
    aguardando_laudo_entrada: t('Aguardando laudo entrada'),
    em_producao: t('Em produção'),
    aguardando_laudo_saida: t('Aguardando laudo saída'),
    pronto: t('Pronto'),
    disponivel: t('Disponível'),
  };
  return map[status] ?? status;
}

const ITEM_STATUS_SEVERITY: Record<string, string> = {
  aguardando_laudo_entrada: 'info',
  em_producao: 'warning',
  aguardando_laudo_saida: 'warning',
  pronto: 'success',
  disponivel: 'info',
};

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

async function loadAll(): Promise<void> {
  log('PDVEntregasPage', 'loadAll', 'Loading all data');
  try {
    const [kpisResult, itemsResult] = await Promise.allSettled([
      fetchEntregasKpis(),
      fetchEntregasItems(),
    ]);

    if (kpisResult.status === 'fulfilled') {
      kpis.value = kpisResult.value;
    } else {
      logError('PDVEntregasPage', 'loadAll', 'KPIs failed', kpisResult.reason);
    }

    if (itemsResult.status === 'fulfilled') {
      rawItems.value = itemsResult.value;
    } else {
      logError('PDVEntregasPage', 'loadAll', 'Items failed', itemsResult.reason);
    }
  } catch (err) {
    logError('PDVEntregasPage', 'loadAll', 'Unexpected error', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados de entregas'));
  }
}

// ---------------------------------------------------------------------------
// Action handler
// ---------------------------------------------------------------------------

function handleItemAction({ data, action }: { value?: unknown; data?: Orders_EntregasItemRow; action?: string }): void {
  if (!data) return;
  if (action === 'notify-whatsapp') {
    if (!data.cliente_whatsapp_phone) {
      eventBus.emit('globalMessage.warning', t('Cliente sem telefone cadastrado'));
      return;
    }
    eventBus.emit('modal.pdv-entregas-notify.open', {
      itemId: data.so_order_item_id,
      customerId: data.so_order_client_id,
    });
  } else if (action === 'deliver') {
    eventBus.emit('modal.pdv-entregas-deliver.open', {
      itemId: data.so_order_item_id,
      customerId: data.so_order_client_id,
    });
  }
}

// ---------------------------------------------------------------------------
// Urgency badge helper
// ---------------------------------------------------------------------------

function urgencyBadge(score: number): { label: string; severity: string } {
  if (score >= 500) return { label: t('Atrasado pronto'), severity: 'danger' };
  if (score >= 400) return { label: t('Atrasado'),         severity: 'danger' };
  if (score >= 300) return { label: t('Avisar'),           severity: 'warning' };
  if (score >= 200) return { label: t('Aguardando cliente'), severity: 'info' };
  if (score >= 150) return { label: t('Próximo do prazo'),  severity: 'info' };
  return { label: t('No prazo'), severity: 'secondary' };
}

// ---------------------------------------------------------------------------
// Days-until-due helper
// ---------------------------------------------------------------------------

function daysUntilDueText(d: number | null | undefined): { text: string; severity: 'danger'|'warning'|'info'|'muted' } {
  if (d == null) return { text: '—', severity: 'muted' };
  if (d <  0)    return { text: `${-d} ${t('dia(s) atrasado')}`, severity: 'danger' };
  if (d === 0)   return { text: t('Hoje'), severity: 'warning' };
  if (d <= 2)    return { text: `${t('Em')} ${d} ${t('dia(s)')}`, severity: 'warning' };
  return            { text: `${t('Em')} ${d} ${t('dia(s)')}`, severity: 'info' };
}

/**
 * Decorate an item row with pre-computed display fields for merged sub-columns.
 * These synthetic fields feed into badge renderTypes and conditional sub-lines
 * in the Prazo and Estado columns.
 */
function decorateEntregasRow(r: Orders_EntregasItemRow): Record<string, unknown> {
  const s = r.urgency_score ?? 0;
  const badgeData = urgencyBadge(s);
  const daysDue = daysUntilDueText(r.days_until_due);
  const isPronto = r.item_status === 'pronto';
  const isProduct = (r as any).so_order_item_type === 'product';
  const notifCount = r.notification_count ?? 0;

  // Products show description/product name instead of service name + priority
  let serviceWithPriority: string;
  if (isProduct) {
    serviceWithPriority = (r as any).so_order_item_description || (r as any).so_product_name || t('Produto');
  } else {
    serviceWithPriority = r.so_service_priority_name
      ? `${r.so_service_name ?? ''} ${r.so_service_priority_name}`.trim()
      : (r.so_service_name ?? '');
  }

  return {
    ...r,
    _service_with_priority: serviceWithPriority,
    _urgency_label: badgeData.label,
    _urgency_severity: badgeData.severity,
    _days_due_text: daysDue.text,
    _days_due_severity: daysDue.severity,
    _status_label: itemStatusLabel(r.item_status),
    _status_severity: ITEM_STATUS_SEVERITY[r.item_status] ?? 'info',
    _pronto_text: isPronto
      ? (r.days_since_ready === 0 ? t('Pronto hoje') : `${t('Pronto há')} ${r.days_since_ready} ${t('dia(s)')}`)
      : '',
    _so_order_label: `Ordem ID: ${r.so_order_id}`,
    _so_order_item_label: `${t('Item ID')}: ${r.so_order_item_id}`,
    _aviso_text: isPronto && notifCount > 0
      ? ((r.days_since_last_notification ?? 0) === 0
          ? `${t('Avisado hoje')} (×${notifCount})`
          : `${t('Avisado há')} ${r.days_since_last_notification} ${t('dia(s)')} (×${notifCount})`)
      : '',
    _aviso_muted_text: isPronto && notifCount === 0
      ? t('Não avisado') : '',
    _entrega_progress: (r._total_items ?? 0) > 0
      ? `${r._delivered_items ?? 0}/${r._total_items} ${t('itens entregues')}`
      : '',
  };
}

// ---------------------------------------------------------------------------
// Column explainers (header info icons)
// ---------------------------------------------------------------------------

const prazoTooltip = computed(() => [
  t('Urgência calculada pelo sistema — é ela que ordena a tabela.'),
  '',
  `• ${t('Atrasado pronto')}: ${t('prazo vencido e item pronto esperando o cliente (pior caso)')}`,
  `• ${t('Atrasado')}: ${t('prazo vencido, ainda em produção ou laudo')}`,
  `• ${t('Avisar')}: ${t('pronto e o cliente ainda não foi avisado')}`,
  `• ${t('Aguardando cliente')}: ${t('pronto e cliente já avisado')}`,
  `• ${t('Próximo do prazo')}: ${t('vence em até 2 dias')}`,
  `• ${t('No prazo')}: ${t('todos os demais')}`,
  '',
  t('Abaixo: dias restantes ou de atraso e a data do prazo.'),
].join('\n'));

const estadoTooltip = computed(() => [
  t('Etapa do item no processo.'),
  '',
  `• ${t('Serviços seguem esta ordem')}: ${t('Aguardando laudo entrada')} → ${t('Em produção')} → ${t('Aguardando laudo saída')} → ${t('Pronto')}`,
  `• ${t('Disponível')}: ${t('produto, sem produção nem laudo — pronto para entrega')}`,
  '',
  t('Avisar aparece só em itens Prontos; Entregar, em Prontos ou Disponíveis.'),
  '',
  t('Abaixo: há quanto tempo está pronto e se o cliente já foi avisado.'),
].join('\n'));

// ---------------------------------------------------------------------------
// Table config
// ---------------------------------------------------------------------------

const itemsTableConfig = computed(() => ({
  tableId: 'pdvEntregasItemsDataTable',
  columns: [
    // -----------------------------------------------------------------------
    // Column 1 — OS (two-line: order id + item id)
    // -----------------------------------------------------------------------
    {
      data: '_so_order_label',
      sortField: 'so_order_id',
      title: t('OS'),
      width: 110,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, rowData: Record<string, unknown>) => {
        eventBus.emit('modal.pdv-checkout.open', { orderId: rowData.so_order_id });
      },
      filterable: true,
      filterType: 'text',
      mergedColumns: [
        {
          data: '_so_order_item_label',
          title: t('Item'),
          renderType: 'custom_clickable_id',
          clickCallback: (_value: unknown, rowData: Record<string, unknown>) => {
            const row = rowData as unknown as Orders_EntregasItemRow;
            eventBus.emit('modal.so-order-item.open', { itemId: row.so_order_item_id });
          },
        },
        {
          data: 'so_order_item_qrcode',
          title: t('QR Code'),
          cellClass: 'cell-subline-muted',
          visible: (row: Record<string, unknown>) => !!row.so_order_item_qrcode,
        },
      ],
    },
    // -----------------------------------------------------------------------
    // Column 2 — Imagem (exit-laudo photo thumbnail; placeholder when none)
    // -----------------------------------------------------------------------
    {
      data: 'item_exit_photo_url',
      title: t('Imagem'),
      width: 72,
      sortable: false,
      filterable: false,
      renderType: 'image',
      imageConfig: {
        width: 44,
        shape: 'rounded' as const,
        preview: true,
        emptyIcon: 'pi pi-image',
        alt: (row: Record<string, unknown>) => String(row.item_description_value ?? row.so_service_name ?? t('Item')),
      },
    },
    // -----------------------------------------------------------------------
    // Column 3 — Cliente (two-line: name + franchise)
    // -----------------------------------------------------------------------
    {
      data: 'so_order_client_name',
      title: t('Cliente'),
      width: 220,
      sortable: true,
      filterable: true,
      filterType: 'text',
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, data: Record<string, unknown>) => {
        const row = data as unknown as Orders_EntregasItemRow;
        eventBus.emit('modal.cliente.open', { id: row.so_order_client_id });
      },
      mergedColumns: [
        { data: 'franquia', title: t('Franquia') },
      ],
    },
    // -----------------------------------------------------------------------
    // Column 3 — Serviço (three-line: "name + priority" + category + description)
    // Master concatenates so_service_name + so_service_priority_name (e.g. "Basic Express").
    // Uses mergedColumns: master field + stacked sub-columns.
    // Filter matches OR across all three fields. CSV exports each as a column.
    // Sort uses the concatenated master field.
    // -----------------------------------------------------------------------
    {
      data: '_service_with_priority',
      title: t('Serviço'),
      width: 220,
      sortable: true,
      filterable: true,
      filterType: 'text',
      mergedColumns: [
        { data: 'so_service_category_name', title: t('Categoria') },
        { data: 'item_description_value', title: t('Descrição') },
      ],
    },
    // -----------------------------------------------------------------------
    // Column 4 — Prazo (urgency badge + days-due + absolute date)
    // -----------------------------------------------------------------------
    {
      data: '_urgency_label',
      sortField: 'urgency_score',
      title: t('Prazo'),
      headerTooltip: prazoTooltip.value,
      headerTooltipTestId: 'pdv-entregas-info-prazo',
      width: 200,
      sortable: true,
      filterable: true,
      filterType: 'text',
      renderType: 'badge',
      badgeConfig: (row: Record<string, unknown>) => ({
        label: (row as any)._urgency_label,
        severity: (row as any)._urgency_severity,
      }),
      mergedColumns: [
        {
          data: '_days_due_text',
          cellClass: (row: Record<string, unknown>) => `cell-days-${(row as any)._days_due_severity}`,
        },
        {
          data: 'so_order_item_expected_delivery_at',
          renderType: 'date',
        },
      ],
    },
    // -----------------------------------------------------------------------
    // Column 5 — Estado (status badge + Pronto há / Aviso)
    // -----------------------------------------------------------------------
    {
      data: '_status_label',
      sortField: 'item_status',
      title: t('Estado'),
      headerTooltip: estadoTooltip.value,
      headerTooltipTestId: 'pdv-entregas-info-estado',
      width: 200,
      sortable: true,
      filterable: true,
      filterType: 'text',
      renderType: 'badge',
      badgeConfig: (row: Record<string, unknown>) => ({
        label: (row as any)._status_label,
        severity: (row as any)._status_severity,
      }),
      mergedColumns: [
        {
          data: '_pronto_text',
          visible: (row: Record<string, unknown>) => (row as any).item_status === 'pronto',
        },
        {
          data: '_aviso_text',
          visible: (row: Record<string, unknown>) => (row as any).item_status === 'pronto' && ((row as any).notification_count ?? 0) > 0,
        },
        {
          data: '_aviso_muted_text',
          cellClass: 'cell-subline-muted',
          visible: (row: Record<string, unknown>) => (row as any).item_status === 'pronto' && ((row as any).notification_count ?? 0) === 0,
        },
        {
          data: '_entrega_progress',
          visible: (row: Record<string, unknown>) => ((row as any)._total_items ?? 0) > 1,
        },
      ],
    },
    // -----------------------------------------------------------------------
    // Column 6 — Ação (two stacked action buttons)
    // -----------------------------------------------------------------------
    {
      data: '__action',
      title: t('Ação'),
      width: 140,
      filterable: false,
      renderType: 'action_buttons',
      buttons: [
        {
          text: t('Avisar'),
          icon: 'pi pi-whatsapp',
          action: 'notify-whatsapp',
          condition: (row: Record<string, unknown>) => {
            const r = row as unknown as Orders_EntregasItemRow;
            return r.item_status === 'pronto' && !!r.cliente_whatsapp_phone && (r as any).so_order_item_type !== 'product';
          },
          testId: (row: Record<string, unknown>) =>
            `pdv-entregas-notify-btn-${(row as unknown as Orders_EntregasItemRow).so_order_item_id}`,
        },
        {
          text: t('Entregar'),
          icon: 'pi pi-check-circle',
          action: 'deliver',
          condition: (row: Record<string, unknown>) => {
            const r = row as unknown as Orders_EntregasItemRow;
            return r.item_status === 'pronto' || r.item_status === 'disponivel';
          },
          testId: (row: Record<string, unknown>) =>
            `pdv-entregas-deliver-btn-${(row as unknown as Orders_EntregasItemRow).so_order_item_id}`,
        },
      ],
    },
  ],
  pagination: true,
  pageLength: 25,
  size: 'small',
  stripedRows: true,
  showGridlines: true,
  sortable: true,
  defaultSortField: 'urgency_score',
  defaultSortOrder: -1 as const,
  showFilters: true,
  filterDisplay: 'row',
  heightMode: 'fill-viewport',
  rowClass: (row: Record<string, unknown>) => {
    const s = (row as unknown as Orders_EntregasItemRow).urgency_score ?? 0;
    if (s >= 400) return 'urgency-row-danger';
    if (s >= 300) return 'urgency-row-warning';
    if (s >= 200) return 'urgency-row-info';
    return '';
  },
}));

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(() => {
  loadAll();
  eventBus.on('filter.updated', loadAll);
  eventBus.on('pdv-entregas.notifications.sent', loadAll);
  eventBus.on('pdv-entregas.items.delivered', loadAll);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', loadAll);
  eventBus.off('pdv-entregas.notifications.sent', loadAll);
  eventBus.off('pdv-entregas.items.delivered', loadAll);
});

defineExpose({ loadAll });
</script>

<style scoped>
.items-section {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.items-section h3 {
  margin: 0 0 12px 0;
  font-size: 1rem;
  font-weight: 600;
}

.empty-state {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--p-text-muted-color, #6b7280);
}

.empty-state i {
  font-size: 1.8rem;
}

.empty-state p {
  margin: 0;
}

.cell-subline       { color: var(--p-text-muted-color, #6b7280); font-size: 0.78rem; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cell-subline-muted { color: var(--p-text-muted-color, #9ca3af); font-size: 0.78rem; font-style: italic; }
.cell-days-danger   { color: var(--p-red-600,    #dc2626); font-weight: 500; }
.cell-days-warning  { color: var(--p-orange-600, #ea580c); font-weight: 500; }
.cell-days-info     { color: var(--p-blue-600,   #2563eb); }
.cell-days-muted    { color: var(--p-text-muted-color, #9ca3af); }

:deep(.urgency-row-danger  > td) { background: rgba(220, 38, 38, 0.06); }
:deep(.urgency-row-warning > td) { background: rgba(234, 88, 12, 0.06); }
:deep(.urgency-row-info    > td) { background: rgba(37, 99, 235, 0.05); }
</style>
